import { hasMeasurements } from 'feature/measurements/util'
import { InferenceStore } from 'store/inference/InferenceStore'
import { opt } from 'store/opt'
import ProfileStore from 'store/profile/ProfileStore'
import { getShouldersConfig, IdentityMode } from 'type/Identity'
import { ShowoffTags } from 'type/Inference'
import { notNil } from 'util/null'
import { parseFinite } from 'util/number'

export type Dimensions = {
  gender: string | undefined
  body_type: string | undefined
  height: number | undefined
  shoulders: number | undefined
  bust: number | undefined
  waist: number | undefined
  hips: number | undefined
  legLength: number | undefined
}

function avg(arr: number[] | undefined): number | undefined {
  if (!arr || arr.length === 0) return undefined
  const sum = arr.reduce((a, b) => a + b, 0)
  return Math.round(sum / arr.length)
}

function extractTagsDimensions(gender: string, tags: ShowoffTags): Dimensions | undefined {
  const identity = tags.data?.identity
  if (!identity) return undefined

  const { identity_mode, dimensions, extended_shape, shape_dimensions } = identity

  const base = identity_mode === 0
  const detailed = identity_mode === 2
  const simple = identity_mode === 3
  if (base || simple) return undefined

  if (detailed && dimensions) {
    const { height, shoulders, chest: bust, waist, hips, legLength } = dimensions
    return { gender, body_type: undefined, height, shoulders, bust, waist, hips, legLength }
  }

  if (extended_shape && Object.keys(extended_shape).length) {
    const { legLength, shape_type } = extended_shape
    const height = avg(extended_shape.height?.split('-').map(parseFinite).filter(notNil))
    return { gender, body_type: shape_type, height, shoulders: undefined, bust: undefined, waist: undefined, hips: undefined, legLength }
  }

  if (shape_dimensions && Object.keys(shape_dimensions).length) {
    const height = avg(shape_dimensions.height)
    const shoulders = gender ? getShouldersConfig(gender).def : undefined
    const bust = avg(shape_dimensions.chest)
    const waist = avg(shape_dimensions.waist)
    const hips = avg(shape_dimensions.hips)
    const legLength = avg(shape_dimensions.legLength)
    return { gender, body_type: undefined, height, shoulders, bust, waist, hips, legLength }
  }

  return undefined
}

function extractProfileDimensions(gender: string, profile: ProfileStore): Dimensions {
  const { mode, shape, height, shoulders, chest, waist, hips, legExtended, legDetailed } = profile.form
  const detailed = mode === IdentityMode.DETAILED
  const legLength = detailed ? legDetailed : legExtended
  return { gender, body_type: shape, height, shoulders, bust: chest, waist, hips, legLength }
}

export function extractDimensions(inference: InferenceStore): Dimensions | undefined {
  const profile = hasMeasurements(inference)
  const item = inference.items[0]
  const gender = item?.dress_gender || profile?.gender || opt.options.gender || 'female'
  if (profile) return extractProfileDimensions(gender, profile)
  const tags = inference.showoff_tags
  if (tags) return extractTagsDimensions(gender, tags)
  return undefined
}
